import * as React from 'react';
import {
  Dialog,
  DialogContent,
} from '../primitives/dialog';
import { Input } from '../primitives/input';
import { Label } from '../primitives/label';
import { Textarea } from '../primitives/textarea';
import { FormShell } from './form-shell';

/**
 * 21 CFR Part 11 e-signature capture surface — the single shared signing dialog
 * for every GxP module.
 *
 * Promoted into the ui-kit from compliance-ui's `SignatureCaptureModal` so that
 * NO module hand-rolls a signing dialog. The alternative that existed before —
 * a free-text name + 4-dot PIN with no re-authentication and no signing meaning —
 * is NOT a Part 11 signature; those surfaces must be migrated to this component.
 *
 * The modal owns its presentational concerns plus the §11.200 step-up credential
 * field: when an `onReAuthenticate` resolver is supplied it renders a required
 * password input, exchanges the entered password for a re-authentication PROOF,
 * and only then invokes `onConfirm`. The parent holds the open / role / meaning
 * state, the reason value, and the submit handler.
 *
 * Also exposed via {@link ConfirmActionDialog}'s `eSignatureSlot` for verb-led
 * confirmation flows that must additionally sign.
 */
export interface ESignatureModalProps {
  /** Whether the dialog is open. */
  open: boolean;
  /**
   * Part 11 signing role being applied (e.g. "Author", "Approver",
   * "Technician"). Named `signingRole` — not `role` — to avoid colliding with
   * the reserved DOM `role`/ARIA attribute.
   */
  signingRole: string;
  /** Subject reference shown in the description (e.g. the protocol / work-order id). */
  subjectReference: string;
  /** Current reason text. */
  reason: string;
  /** Whether the sign mutation is in flight. */
  submitting: boolean;
  onReasonChange: (value: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
  /**
   * Optional §11.200 step-up resolver. When provided, the modal renders a
   * required credential field and calls this with the entered password to
   * obtain / stash a re-authentication proof BEFORE invoking `onConfirm`.
   * Resolve `true` once the proof is obtained; resolve `false` (or reject) to
   * keep the dialog open and surface a credential error. When omitted the dialog
   * behaves as a plain reason-only capture.
   */
  onReAuthenticate?: (credential: string) => Promise<boolean>;
}

export const ESignatureModal: React.FC<ESignatureModalProps> = ({
  open,
  signingRole,
  subjectReference,
  reason,
  submitting,
  onReasonChange,
  onConfirm,
  onCancel,
  onReAuthenticate,
}) => {
  const requireReAuth = typeof onReAuthenticate === 'function';
  const [credential, setCredential] = React.useState('');
  const [reAuthBusy, setReAuthBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const reasonRef = React.useRef<HTMLTextAreaElement>(null);
  const credentialRef = React.useRef<HTMLInputElement>(null);

  // Reset the transient credential state whenever the dialog (re)opens so a
  // previously typed password is never retained across signings.
  React.useEffect(() => {
    if (open) {
      setCredential('');
      setReAuthBusy(false);
      setError(null);
    }
  }, [open]);

  // Move focus to the action-critical field when the dialog opens so keyboard
  // and screen-reader users land on the right input without tabbing: the §11.200
  // step-up credential when re-authentication is required, otherwise the reason
  // field. Deferred a tick so it runs after the dialog's own initial focus.
  React.useEffect(() => {
    if (!open) return undefined;
    const id = window.setTimeout(() => {
      if (requireReAuth) credentialRef.current?.focus();
      else reasonRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(id);
  }, [open, requireReAuth]);

  const handleConfirm = React.useCallback(async () => {
    if (!requireReAuth) {
      onConfirm();
      return;
    }
    if (!credential.trim()) {
      setError('Enter your password to re-authenticate this signature.');
      return;
    }
    setReAuthBusy(true);
    setError(null);
    try {
      const ok = await onReAuthenticate!(credential);
      if (!ok) {
        setError('Re-authentication failed. Check your password and try again.');
        return;
      }
      onConfirm();
    } catch {
      setError('Re-authentication failed. Check your password and try again.');
    } finally {
      setReAuthBusy(false);
    }
  }, [requireReAuth, credential, onReAuthenticate, onConfirm]);

  const errors = error ? [{ message: error }] : [];

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <DialogContent className="max-w-md p-0">
        <FormShell
          eyebrow="E-signature"
          title={`Apply ${signingRole} signature`}
          description={`Recording electronic signature on ${subjectReference}.`}
          errors={errors}
          dirty
          submitting={submitting || reAuthBusy}
          onSave={handleConfirm}
          onCancel={onCancel}
          saveLabel={reAuthBusy ? 'Re-authenticating…' : 'Confirm + sign'}
        >
          <div className="grid grid-cols-1 gap-4">
            <div className="space-y-2">
              <Label htmlFor="sig-reason">Reason *</Label>
              <Textarea
                ref={reasonRef}
                id="sig-reason"
                rows={3}
                value={reason}
                onChange={(e) => onReasonChange(e.target.value)}
                placeholder="Reviewed all evidence; work meets specification."
              />
            </div>
            {requireReAuth && (
              <div className="space-y-2">
                <Label htmlFor="sig-credential">Password *</Label>
                <Input
                  ref={credentialRef}
                  id="sig-credential"
                  type="password"
                  autoComplete="current-password"
                  value={credential}
                  onChange={(e) => setCredential(e.target.value)}
                  placeholder="Re-enter your password to sign"
                />
                <p className="text-xs text-muted-foreground">
                  21 CFR Part 11 requires you to re-authenticate for each
                  electronic signature.
                </p>
                {/* Polite live region for the commit/re-auth outcome so a
                    screen-reader signer hears a re-authentication refusal as it
                    is surfaced, mirroring the FormShell error banner. */}
                <p aria-live="polite" className="text-xs text-destructive">
                  {error ?? ''}
                </p>
              </div>
            )}
          </div>
        </FormShell>
      </DialogContent>
    </Dialog>
  );
};

export default ESignatureModal;
